/**
 * ContentContext
 *
 * Context for managing content-specific state:
 * - Pages cache
 * - Categories data
 * - Images data
 * - Content refresh functions
 */

import type { ReactNode } from 'react';
import React, { createContext, useContext, useState, useCallback } from 'react';

import type { PageContent, Category, Image } from '../services/api';
import api from '../services/api';

/**
 * ContentContext State Interface
 */
export type ContentContextState = {
  // Pages cache (keyed by slug)
  readonly pages: Record<string, PageContent>;
  readonly getPage: (slug: string) => PageContent | null;
  readonly loadPage: (slug: string) => Promise<PageContent>;
  readonly clearPagesCache: () => void;

  // Categories
  readonly categories: Record<string, ReadonlyArray<Category>>; // Keyed by type (portfolio, shop)
  readonly loadCategories: (type: 'portfolio' | 'shop') => Promise<ReadonlyArray<Category>>;
  readonly getAllCategories: () => Promise<ReadonlyArray<Category>>;

  // Images
  readonly images: Record<string, ReadonlyArray<Image>>; // Keyed by category ID
  readonly loadImages: (categoryId: string) => Promise<ReadonlyArray<Image>>;
  readonly getAllImages: () => Promise<ReadonlyArray<Image>>;
  readonly clearImagesCache: () => void;

  // Update operations
  readonly updateContent: (slug: string, data: Partial<PageContent>) => Promise<PageContent>;
  readonly refreshContent: () => Promise<void>;

  // Loading states
  readonly isLoading: boolean;
  readonly error: string | null;
};

/**
 * Create the context with undefined default (will be provided by ContentProvider)
 */
const ContentContext = createContext<ContentContextState | undefined>(undefined);

/**
 * ContentProvider Props
 */
type ContentProviderProps = {
  readonly children: ReactNode;
};

/**
 * ContentProvider Component
 *
 * Provides content management and caching functionality
 * @param root0
 * @param root0.children
 */
export const ContentProvider: React.FC<ContentProviderProps> = ({ children }) => {
  // Pages cache
  const [pages, setPages] = useState<Record<string, PageContent>>({});

  // Categories cache
  const [categories, setCategories] = useState<Record<string, ReadonlyArray<Category>>>({});

  // Images cache
  const [images, setImages] = useState<Record<string, ReadonlyArray<Image>>>({});

  // Loading and error state
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Get cached page by slug
   */
  const getPage = useCallback(
    (slug: string): PageContent | null => {
      return pages[slug] || null;
    },
    [pages]
  );

  /**
   * Load page content by slug (with caching)
   */
  const loadPage = useCallback(
    async (slug: string): Promise<PageContent> => {
      // Return cached page if available
      if (pages[slug]) {
        return pages[slug];
      }

      setIsLoading(true);
      setError(null);

      try {
        const pageContent = await api.content.getBySlug(slug);

        // Cache the page
        setPages((prev) => ({
          ...prev,
          [slug]: pageContent,
        }));

        return pageContent;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : `Failed to load page: ${slug}`;
        setError(errorMessage);
        console.error(`Error loading page ${slug}:`, err);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [pages]
  );

  /**
   * Clear pages cache
   */
  const clearPagesCache = useCallback(() => {
    setPages({});
  }, []);

  /**
   * Load categories by type (with caching)
   */
  const loadCategories = useCallback(
    async (type: 'portfolio' | 'shop'): Promise<ReadonlyArray<Category>> => {
      // Return cached categories if available
      if (categories[type]) {
        return categories[type];
      }

      setIsLoading(true);
      setError(null);

      try {
        const response = await api.categories.getByType(type);
        const categoryList = response.categories;

        // Sort by order
        const sortedCategories = [...categoryList].sort(
          (a: Category, b: Category) => a.order - b.order
        );

        // Cache the categories
        setCategories((prev) => ({
          ...prev,
          [type]: sortedCategories,
        }));

        return sortedCategories;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : `Failed to load categories: ${type}`;
        setError(errorMessage);
        console.error(`Error loading categories for ${type}:`, err);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [categories]
  );

  /**
   * Get all categories (no type filter)
   */
  const getAllCategories = useCallback(async (): Promise<ReadonlyArray<Category>> => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await api.categories.getAll();
      return [...response.categories].sort((a, b) => a.order - b.order);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load all categories';
      setError(errorMessage);
      console.error('Error loading all categories:', err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Load images by category (with caching)
   */
  const loadImages = useCallback(
    async (categoryId: string): Promise<ReadonlyArray<Image>> => {
      // Return cached images if available
      if (images[categoryId]) {
        return images[categoryId];
      }

      setIsLoading(true);
      setError(null);

      try {
        // Use getImages which supports pagination and category slug
        const response = await api.images.getImages(categoryId, 1, 100);
        const imageList = response.images;

        // Sort by order (backend already sorts, but double-check)
        const sortedImages = [...imageList].sort((a, b) => a.order - b.order);

        // Cache the images
        setImages((prev) => ({
          ...prev,
          [categoryId]: sortedImages,
        }));

        return sortedImages;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : `Failed to load images for category: ${categoryId}`;
        setError(errorMessage);
        console.error(`Error loading images for category ${categoryId}:`, err);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [images]
  );

  /**
   * Get all images (no category filter)
   */
  const getAllImages = useCallback(async (): Promise<ReadonlyArray<Image>> => {
    setIsLoading(true);
    setError(null);

    try {
      const imageList = await api.images.getAll();
      return [...imageList].sort((a, b) => a.order - b.order);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load all images';
      setError(errorMessage);
      console.error('Error loading all images:', err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Clear images cache
   */
  const clearImagesCache = useCallback(() => {
    setImages({});
  }, []);

  /**
   * Update content for a page
   */
  const updateContent = useCallback(
    async (slug: string, data: Partial<PageContent>): Promise<PageContent> => {
      setIsLoading(true);
      setError(null);

      try {
        const updatedContent = await api.content.update(slug, data);

        // Update cache
        setPages((prev) => ({
          ...prev,
          [slug]: updatedContent,
        }));

        return updatedContent;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : `Failed to update content: ${slug}`;
        setError(errorMessage);
        console.error(`Error updating content for ${slug}:`, err);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  /**
   * Refresh all content (clear caches)
   */
  const refreshContent = useCallback(async (): Promise<void> => {
    clearPagesCache();
    clearImagesCache();
    setCategories({});
  }, [clearPagesCache, clearImagesCache]);

  /**
   * Context value
   */
  const value: ContentContextState = {
    // Pages
    pages,
    getPage,
    loadPage,
    clearPagesCache,

    // Categories
    categories,
    loadCategories,
    getAllCategories,

    // Images
    images,
    loadImages,
    getAllImages,
    clearImagesCache,

    // Updates
    updateContent,
    refreshContent,

    // Loading and error
    isLoading,
    error,
  };

  return <ContentContext.Provider value={value}>{children}</ContentContext.Provider>;
};

/**
 * useContent Hook
 *
 * Custom hook to access ContentContext
 * Throws error if used outside of ContentProvider
 */
// eslint-disable-next-line react-refresh/only-export-components
export const useContent = (): ContentContextState => {
  const context = useContext(ContentContext);

  if (context === undefined) {
    throw new Error('useContent must be used within a ContentProvider');
  }

  return context;
};

export default ContentContext;
